import React from 'react';
import { ChildProps, MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import {
  CorePacklinkCreateDraft,
  OrderPacklinkShipmentData,
  core_apimessages_PacklinkShipment_Status as PacklinkShipmentStatus,
} from '../schemas/rql';
import ConnectingToPacklink from './packlink_connecting_to_packlink';
import usePrevious from '@reverbdotcom/commons/src/use_previous_hook';

interface ExternalProps {
  onDraftSuccess(): void;
  order: OrderPacklinkShipmentData.Fragment;
  startPolling(): void;
  stopPolling(): void;
}

interface MutationProps {
  mutate: MutationFn<CorePacklinkCreateDraft.Mutation, CorePacklinkCreateDraft.Variables>;
}

export type Props = ChildProps<ExternalProps> & MutationProps;

export function PacklinkCreateDraft(props: Props) {
  const {
    order,
    onDraftSuccess,
    startPolling,
    stopPolling,
    mutate,
  } = props;

  // eslint-disable-next-line no-restricted-globals
  const currentStatus = order?.packlinkShipment?.status;
  const previousStatus = usePrevious(currentStatus);

  const terminalStatuses = [PacklinkShipmentStatus.SUCCESS, PacklinkShipmentStatus.FAILED];
  const hasTerminalStatus = currentStatus && terminalStatuses.includes(currentStatus);

  React.useEffect(() => {
    async function createDraft() {
      if (hasTerminalStatus) {
        return;
      }
      const response = await mutate({ variables: { input: { orderId: order.legacyOrderId } } });

      if (response) {
        startPolling();
      }
    }

    createDraft();
  }, [currentStatus, mutate, order.legacyOrderId, startPolling, hasTerminalStatus]);

  React.useEffect(() => {
    function stopPollingOnSuccess() {
      const previouslyNotSuccessful = previousStatus !== PacklinkShipmentStatus.SUCCESS;
      const currentlySuccessful = currentStatus === PacklinkShipmentStatus.SUCCESS;
      const statusChangedToSuccess = previouslyNotSuccessful && currentlySuccessful;

      if (statusChangedToSuccess) {
        stopPolling();
      }
    }

    stopPollingOnSuccess();
  }, [previousStatus, currentStatus, order, onDraftSuccess, stopPolling]);

  return (
    <ConnectingToPacklink />
  );
}

const withMutation = withGraphql<ExternalProps, CorePacklinkCreateDraft.Mutation, CorePacklinkCreateDraft.Variables>(gql`
mutation Core_Packlink_CreateDraft($input: Input_core_apimessages_CreatePacklinkDraftRequest) {
  createPacklinkDraft(input: $input) {
    shipment {
      id
    }
  }
}`,
);

export default withMutation(PacklinkCreateDraft);
